import { memo, useState, useEffect, useMemo, MouseEvent } from 'react';
import { useMediaQuery, useTheme, Badge } from '@material-ui/core';
import { MenuItem, NotificationState, Portal } from 'models';
import { routes } from 'configs';
import { toUpperFirstChar } from 'utils';
import _keyBy from 'lodash/keyBy';
import _get from 'lodash/get';
import clsx from 'clsx';
import {
  modifyMenu,
  pathToId,
  countParentNotifications,
  countChildNotification,
} from './helpers';

import { NavLink, useLocation, useHistory } from 'react-router-dom';
import Collapse from '@material-ui/core/Collapse';
import { PremisIcon, ToggleButton } from 'components';

import { useStyles } from './style';

interface Props {
  portal: Portal | null;
  menu: MenuItem[];
  notifications: NotificationState;
  handleCloseMenu?: () => void;
}

const Navigation: React.FC<Props> = ({
  portal,
  menu,
  handleCloseMenu,
  notifications,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const routesDictionary = useMemo(() => _keyBy(routes, 'id'), []);

  const [openedParent, setOpenedParent] = useState<string | null>(null);

  const modifiedMenu = useMemo(() => {
    return modifyMenu(menu);
  }, [menu]);

  useEffect(() => {
    const id = pathToId(location.pathname);
    if (id === '') {
      setOpenedParent(null);
    } else {
      const menuItem = menu.find((menuItem) => {
        return menuItem.Id === id;
      });

      if (menuItem) {
        setOpenedParent(menuItem.Parent);
      }
    }

    /* eslint-disable-next-line */
  }, [location, matches, modifiedMenu]);

  const handleChangeOpenedParent = (id: string) => {
    setOpenedParent((prevState) => (prevState === id ? null : id));
  };

  const handleClickHomePage = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (matches && handleCloseMenu) {
      handleCloseMenu();
    }

    history.push('/');
  };

  const showHomePageMenu =
    portal?.Name === 'Maintenance' || portal?.Name === 'Default';

  return (
    <div className={classes.root}>
      <ul className={classes.ul}>
        <li className={classes.parent}>
          {showHomePageMenu && (
            <NavLink
              to="/"
              className={clsx(classes.child, classes.mainLink)}
              activeClassName={clsx(
                classes.activeChild,
                classes.activeMainLink
              )}
              exact
              onClick={handleClickHomePage}
            >
              KOTI
            </NavLink>
          )}
        </li>
        {Object.values(modifiedMenu).map((item) => {
          const { id, label, childrens } = item;
          const active = openedParent === id;
          const parentNotificationsAmount = countParentNotifications(
            childrens,
            notifications
          );

          return (
            <li key={id} className={classes.parent}>
              <div
                className={classes.contentWrapper}
                onClick={() => handleChangeOpenedParent(id)}
              >
                <Badge
                  max={9}
                  badgeContent={parentNotificationsAmount}
                  classes={{
                    anchorOriginTopRightRectangle:
                      classes.anchorOriginTopRightRectangle,
                  }}
                  children={
                    <span className={classes.label}>
                      {toUpperFirstChar(label)}
                    </span>
                  }
                />
                <ToggleButton dir={active ? 'up' : 'down'} />
              </div>
              <Collapse in={active} timeout="auto" unmountOnExit>
                <ul className={classes.ul}>
                  {childrens
                    .filter((child) => {
                      const type = _get(routesDictionary, child.id)?.type;

                      return type === 1;
                    })
                    .map((child) => {
                      const routeInfo = _get(routesDictionary, child.id);
                      const childNotificationsAmount = countChildNotification(
                        child.id,
                        notifications
                      );
                      return (
                        <li key={child.id} onClick={handleCloseMenu}>
                          {routeInfo.href ? (
                            <a
                              className={classes.child}
                              target="_blank"
                              href={routeInfo.href}
                              rel="noreferrer"
                            >
                              {child.label}
                            </a>
                          ) : (
                            <NavLink
                              to={`/${child.id}`}
                              className={classes.child}
                              activeClassName={classes.activeChild}
                              exact
                            >
                              <Badge
                                max={9}
                                badgeContent={childNotificationsAmount}
                                classes={{
                                  anchorOriginTopRightRectangle:
                                    classes.anchorOriginTopRightRectangle,
                                }}
                                children={child.label}
                              />
                            </NavLink>
                          )}
                        </li>
                      );
                    })}
                </ul>
              </Collapse>
            </li>
          );
        })}
      </ul>
      {smDown ? null : (
        <div className={classes.premisIconWrapper}>
          <span className={classes.poweredBy}>Powered by</span>
          <PremisIcon />
        </div>
      )}
    </div>
  );
};

export default memo(Navigation);
